import {
  AlarmClock,
  Check,
  CheckCircle2,
  CopyPlus,
  Factory,
  ChevronDown,
  ChevronUp,
  Link2,
  MessageCircle,
  Minus,
  Send,
  Ship,
  Truck,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  PlanningCard,
  PlanningPickIntent,
  PlanningPickup,
} from "../../types/planning";
import {
  boxNumber,
  canMove,
  canUnassign,
  cardCode,
} from "../../utils/planningBoard";
import { PlanningCardMap } from "./PlanningCardMap";
import { PartnerColorDot } from "../partners/PartnerColorPicker";
import { partnerStripe } from "../../constants/partnerColors";
import HandlerBubble from "../handler/HandlerBubble";

/**
 * One container, as one card — the same component in the waiting pool and in a
 * truck's slot.
 *
 * Shared on purpose. A dispatcher drags a card from the left of the screen to
 * the right and it has to stay recognisably the same object; two components
 * would drift apart on exactly the details that make it recognisable — which
 * line is bold, where the deadline sits, what the status dot means. The two
 * contexts differ only in which actions the card offers, so that is the only
 * thing this branches on.
 *
 * ── The three states of the send row ─────────────────────────────────────────
 *
 * Nothing in the pool: a container with no truck cannot be sent to one, and a
 * button that always refuses is worse than no button.
 *
 * In a slot, not yet sent: the primary action on the card. This is the whole
 * point of the screen — placing was silent, and this is the press that puts the
 * box on a screen in a cab.
 *
 * In a slot, already sent: a badge with the time, and a quiet "take it back"
 * beside it. The withdrawal is deliberately not styled as an equal action; it
 * is a correction, and it stops being possible the moment the driver stamps the
 * first step.
 */

/** What an absent value looks like, everywhere on this card. */
export const EMPTY = "\u2014";

/**
 * `YYYY-MM-DD` as `DD.MM.YYYY`.
 *
 * With the year, which the board used to drop. A container board routinely
 * spans a year boundary — a cut-off in the first week of January is planned
 * against in December — and "02.01" on a card is then genuinely ambiguous about
 * which January it means. Four characters is a cheap price for a date nobody
 * has to work out.
 */
export const planDate = (day?: string | null) => {
  if (!day || day.length < 10) return null;
  return `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
};

/**
 * The client's appointment at the loading site, as one value.
 *
 * Each half stands on its own: a booked date with no agreed time shows the date
 * and an em dash, not a half-sentence that reads as though the time were
 * unknown-but-soon. Neither half is ever substituted for the other — a date in
 * the time column would be worse than nothing, because somebody would plan a
 * morning around it.
 *
 * Both absent collapses to a single dash rather than "— —", which looks like a
 * rendering fault rather than an unbooked slot.
 */
export const onSiteSlot = (card: {
  loadingSite?: { date?: string | null; time?: string | null } | null;
}) => {
  const day = planDate(card.loadingSite?.date);
  const time = card.loadingSite?.time?.trim() || null;
  if (!day && !time) return EMPTY;
  return `${day ?? EMPTY} ${time ?? EMPTY}`;
};

/** The dot in the corner: how far through its run this container is. */
const TONE: Record<string, string> = {
  created: "bg-gray-300",
  planned: "bg-secondary-500",
  completed: "bg-green-500",
  cancelled: "bg-gray-300",
};

const toneOf = (card: PlanningCard) =>
  TONE[card.status] ?? (card.started ? "bg-amber-500" : "bg-secondary-500");

/**
 * Loaded, empty, or nobody has said — drawn as a solid, an outlined and a
 * dashed pill, so the three read apart at a glance without leaning on colour
 * the board already spends on deadlines, holds and releases.
 */
const LOAD_PILL: Record<"empty" | "full" | "none", string> = {
  full: "border-dark-900 bg-dark-900 text-white",
  empty: "border-gray-400 bg-white text-dark-900",
  none: "border-dashed border-gray-300 bg-gray-50 text-gray-500",
};

/** The load-status pill, shared with the pool's row view. */
export function LoadStatusPill({ status }: { status: PlanningCard["loadStatus"] }) {
  const { t } = useTranslation("calendar");
  const key = status ?? "none";
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide ${LOAD_PILL[key]}`}
    >
      {t(`planning.load.${key}`)}
    </span>
  );
}

interface Props {
  card: PlanningCard;
  /**
   * The plate of the truck this container already carries, when it is sitting
   * in the pool with one. Null everywhere else.
   */
  assignedPlate?: string | null;
  /** Where this card is being drawn — decides which actions it offers. */
  context: "pool" | "slot";
  /** True while this card is the one the dispatcher has picked up. */
  picked?: boolean;
  /** How many board cells this container holds. Slot context only. */
  slotCount?: number;
  /** True while this container's slots are being edited. */
  editingSlots?: boolean;
  /** True while a write concerning this card is in flight. */
  busy?: boolean;

  onPick: (pickup: PlanningPickup, intent?: PlanningPickIntent) => void;
  onSend?: (card: PlanningCard) => void;
  onUnsend?: (card: PlanningCard) => void;
  onUnassign?: (card: PlanningCard) => void;
  onOpen?: (card: PlanningCard) => void;
  /**
   * True while this card is opened out to show where its container goes.
   *
   * Owned by the page, not by the card, so exactly one is ever open: the board
   * draws hundreds of these and an open card holds a live map. That single
   * open card is also why this is an expansion rather than a dialog — the card
   * stays where it is, in the row and the day it belongs to, and the map is
   * read in place instead of covering the board the dispatcher is reading it
   * against.
   */
  mapOpen?: boolean;
  /** Open this card's map, or close it if it is the one already open. */
  onToggleMap?: (card: PlanningCard) => void;
  /** Start (or stop) editing which slots this container holds. */
  onEditSlots?: (card: PlanningCard) => void;
  /** Free the slot this card is drawn in, keeping the container's others. */
  onFreeSlot?: () => void;
  /**
   * The handler's initials, resolved by the page.
   *
   * Passed in rather than read from the card, because a card returned by a
   * board mutation carries its handler's id and no name — those endpoints build
   * their answer inside a transaction and do not pay for a lookup. The page
   * holds the roster anyway, for the filter.
   */
  handlerLabel?: string;
}

export function PlanningCardView({
  card,
  assignedPlate = null,
  context,
  picked = false,
  slotCount = 1,
  editingSlots = false,
  busy = false,
  onPick,
  onSend,
  onUnsend,
  onUnassign,
  onOpen,
  mapOpen = false,
  onToggleMap,
  onEditSlots,
  onFreeSlot,
  handlerLabel,
}: Props) {
  const { t } = useTranslation("calendar");

  const pickup: PlanningPickup = { card, from: context };

  /**
   * Two questions, not one, and they used to be the same answer.
   *
   * `movable` is where the box sits — drag, slots, free a slot. That stays open
   * for the whole run, because a half-driven container still has a place in a
   * truck's week and a re-route or a breakdown is exactly when it changes.
   *
   * `unassignable` is *whose* it is. That closes the moment a wheel turns: a
   * running container must belong to some truck, so the way to move it off this
   * one is to drop it on another, not to take it off the board.
   *
   * Both are withdrawn rather than shown disabled — the `underway` line under
   * the card says why, once.
   */
  const movable = canMove(card);
  const unassignable = canUnassign(card);
  /** The client partner's colour down the left edge — whose job this is. */
  const stripe = partnerStripe(card.orderClientPartner);

  return (
    <div
      // Draggable only where dragging can work. Touch devices never fire these
      // events, which is exactly why picking up is state and not a gesture —
      // see `PlanningPickup`.
      draggable={movable}
      onDragStart={(event) => {
        if (!movable) return;
        // Firefox refuses to start a drag without payload on the transfer.
        event.dataTransfer.setData("text/plain", card.id);
        event.dataTransfer.effectAllowed = "move";
        // "grab", never "toggle": a drag that began on an already-held card
        // would otherwise put it down and then have nothing to drop.
        onPick(pickup, "grab");
      }}
      onClick={() => movable && onPick(pickup, "toggle")}
      className={[
        "group relative rounded-lg border bg-white p-2.5 text-left transition",
        movable ? "cursor-pointer hover:shadow-md" : "cursor-default",
        picked || editingSlots
          ? "border-secondary-500 ring-2 ring-secondary-200"
          : "border-gray-200",
        busy ? "opacity-60" : "",
        stripe ? "border-l-4" : "",
      ].join(" ")}
      // Inline so it wins over the picked state's border colour: the ring
      // already says "picked", and the stripe must not vanish while it does.
      style={stripe ? { borderLeftColor: stripe } : undefined}
    >
      {/* ── Whose job this is, and how it is going ─────────────────────────
          The status dot and the handler bubble share the top-right corner,
          which is the one place on the card a dispatcher's eye already goes for
          "is this all right?". The handler answers the companion question —
          "and is it mine?" — so the two belong together rather than apart.

          The bubble is deliberately not coloured: the card already carries the
          client's partner colour down its left edge and the status tone in the
          dot beside this, and a third colour system would be competing with the
          one that says something is *wrong*. */}
      <span className="absolute right-2 top-2 flex items-center gap-1.5">
        {card.handler && (
          <HandlerBubble
            handler={
              handlerLabel
                ? {
                    id: card.handler.id,
                    // Split back into the two fields the bubble abbreviates.
                    firstName: handlerLabel.split(" ")[0] ?? "",
                    lastName: handlerLabel.split(" ").slice(1).join(" "),
                  }
                : card.handler
            }
            size="sm"
          />
        )}
        <span
          className={`h-2 w-2 rounded-full ${toneOf(card)}`}
          title={t(`planning.status.${card.status}`, {
            defaultValue: card.status,
          })}
        />
      </span>

      {/*
        ── Where the truck is going ──────────────────────────────────────────

        The headline block is the loading site, and it is now labelled as one.

        It was not, and the card was genuinely ambiguous: three unlabelled lines
        of company and address followed by a fourth company name, with nothing
        saying which was the place and which was the customer. Since the loading
        site moved to the Addresses directory the two are not even the same kind
        of record — one is a place a truck is sent to, the other is a billing
        identity — so the reader has to be told which they are looking at.
      */}
      <div className="flex items-center gap-1 pr-4 text-[9px] font-bold uppercase tracking-wider text-gray-400">
        <Factory className="h-3 w-3 shrink-0" />
        {t("planning.card.loadingSite")}
      </div>
      <div className="pr-4 text-[13px] font-bold leading-tight text-dark-900">
        {card.city || t("planning.card.noCity")}
      </div>
      {card.client && (
        <div className="mt-0.5 truncate text-[11.5px] font-semibold leading-tight text-secondary-700">
          {card.client}
        </div>
      )}
      {card.warehouse && (
        <div className="mt-0.5 truncate text-[11px] leading-tight text-gray-500">
          {card.warehouse}
        </div>
      )}

      {/*
        ── The two facts that are not the loading site ───────────────────────

        Labelled rows rather than more unlabelled lines, because that is the
        whole problem being fixed. Both are always rendered — never suppressed
        for matching the block above — since the client and the loading site are
        drawn from two different directories and two records sharing a name is a
        coincidence, not a duplicate. Hiding one on a string match would have
        blanked the client exactly when both facts were real.

        Each value falls back to an em dash so the rows keep their place: a card
        whose on-site slot is missing should say so, not quietly lose a line and
        leave the reader unsure whether it was never booked or never rendered.
      */}
      <dl className="mt-1.5 space-y-0.5 border-t border-gray-100 pt-1.5">
        <div className="flex items-baseline gap-1.5">
          <dt className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-gray-400">
            {t("planning.card.client")}
          </dt>
          <dd className="flex min-w-0 flex-1 items-center justify-end gap-1.5 text-[10.5px] font-semibold text-gray-700">
            {card.orderClient && <PartnerColorDot color={stripe} />}
            <span className="truncate">{card.orderClient || EMPTY}</span>
          </dd>
        </div>
        <div className="flex items-baseline gap-1.5">
          <dt className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-gray-400">
            {t("planning.card.onSite")}
          </dt>
          <dd className="min-w-0 flex-1 truncate text-right font-mono text-[10.5px] font-semibold text-secondary-700">
            {onSiteSlot(card)}
          </dd>
        </div>
      </dl>

      {card.deadline && (
        <div
          className="mt-1 flex items-center gap-1 font-mono text-[10.5px] font-semibold text-orange-700"
          title={card.deadline.label ?? undefined}
        >
          <AlarmClock className="h-3 w-3 shrink-0" />
          {/* The year, for the reason `planDate` gives: a board routinely spans
              a year boundary and "02.01" is ambiguous on it. */}
          {planDate(card.deadline.day)}
          {card.deadline.time ? ` ${card.deadline.time}` : ""}
        </div>
      )}

      {/* What the box is and whose it is — the two things a terminal asks. */}
      <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
        <LoadStatusPill status={card.loadStatus} />
        <span
          className={`flex min-w-0 items-center gap-1 text-[10.5px] font-semibold ${
            card.shippingLine ? "text-gray-700" : "text-gray-400"
          }`}
          title={card.shippingLine ?? undefined}
        >
          <Ship className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {card.shippingLine || t("planning.card.noShippingLine")}
          </span>
        </span>
      </div>

      {/*
        A container in the pool that already has a truck.

        It reaches the pool by being given a truck on its own page, which sets
        no board position — so it is genuinely unplanned and genuinely belongs
        here. But without saying so the card is indistinguishable from one that
        has no truck at all, and dragging it onto another truck silently
        reassigns it. The chip is the warning, and it only ever appears in the
        pool: in a slot the truck is the row the card is sitting in.
      */}
      {context === "pool" && assignedPlate && (
        <div className="mt-1.5 flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
          <Truck className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {t("planning.card.onTruck", { plate: assignedPlate })}
          </span>
        </div>
      )}

      {/* Holding more than one slot — the other cells say "continued". */}
      {context === "slot" && slotCount > 1 && (
        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-secondary-50 px-1.5 py-0.5 text-[10px] font-semibold text-secondary-700">
          <Link2 className="h-3 w-3" />
          {t("planning.card.slotCount", { count: slotCount })}
        </div>
      )}

      {/*
        The id first, the box second.
        
        They were the other way round, which made the name a container wore
        depend on whether its box number had been typed in yet — and put a
        different name on the board than the map showed for the same container.
        See `cardCode`.
      */}
      <div className="mt-1.5 flex items-baseline justify-between gap-2 border-t border-gray-100 pt-1">
        <span className="font-mono text-[11px] font-semibold text-dark-900">
          {cardCode(card)}
        </span>
        <span className="truncate font-mono text-[10px] text-gray-400">
          {boxNumber(card)}
        </span>
      </div>

      {card.unread > 0 && (
        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
          <MessageCircle className="h-3 w-3" />
          {card.unread}
        </span>
      )}

      {/* ── The send row ─────────────────────────────────────────────────── */}
      {context === "slot" && !card.sent && (
        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            onSend?.(card);
          }}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-secondary-600 px-2 py-1.5 text-[10.5px] font-bold text-white transition hover:bg-secondary-700 disabled:opacity-50"
        >
          <Send className="h-3 w-3" />
          {t("planning.card.send")}
        </button>
      )}

      {/*
        The badge is shown in both contexts, unlike the button above it.
        Sending needs a truck to send to, so it is a slot-only action — but
        being *sent* is a state a waiting container can now be in, because
        taking a container off its truck no longer withdraws its release. A
        pool card with no badge would read as unsent and be wrong.
      */}
      {card.sent && (
        <div className="mt-2 flex items-center gap-1">
          <span className="flex flex-1 items-center justify-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-[10px] font-bold text-green-700">
            <Check className="h-3 w-3" />
            {t("planning.card.sent")}
            {card.sentAt ? ` · ${new Date(card.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
          </span>
          {/*
           * Withdrawing the release only exists while nothing has been driven.
           * Once the driver stamps a step the server refuses it, and a button
           * that is going to be refused should not be on the screen.
           */}
          {!card.started && (
            <button
              type="button"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                onUnsend?.(card);
              }}
              title={t("planning.card.unsendHint")}
              className="rounded-md border border-gray-200 px-1.5 py-1.5 text-gray-500 transition hover:border-orange-300 hover:text-orange-700 disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* ── Row-level actions, revealed on hover / always on touch ───────── */}
      {/*
        Freeing this card's own cell, offered only while editing slots — out of
        that mode the whole container moves or unassigns, and a third way to
        take it off a slot would crowd the two that matter.
      */}
      {context === "slot" && editingSlots && movable && onFreeSlot && (
        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            onFreeSlot();
          }}
          title={t("planning.slot.freeHint")}
          className="mt-1.5 flex w-full items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[10px] font-semibold text-gray-600 transition hover:border-orange-300 hover:text-orange-700 disabled:opacity-50"
        >
          <Minus className="h-3 w-3" />
          {t("planning.slot.freeThis")}
        </button>
      )}

      <div
        className={`mt-1.5 flex flex-wrap items-center justify-end gap-1 transition ${
          editingSlots
            ? "opacity-100"
            : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100"
        }`}
      >
        {onToggleMap && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleMap(card);
            }}
            title={t("planning.card.mapHint")}
            className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold transition ${
              mapOpen
                ? "border-secondary-500 bg-secondary-600 text-white"
                : "border-gray-200 text-gray-500 hover:border-secondary-300 hover:text-secondary-700"
            }`}
          >
            {mapOpen ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {t("planning.card.map")}
          </button>
        )}
        {onOpen && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(card);
            }}
            className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 transition hover:border-secondary-300 hover:text-secondary-700"
          >
            {t("planning.card.open")}
          </button>
        )}
        {context === "slot" && movable && onEditSlots && (
          <button
            type="button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onEditSlots(card);
            }}
            title={t("planning.card.slotsHint")}
            className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold transition disabled:opacity-50 ${
              editingSlots
                ? "border-secondary-500 bg-secondary-600 text-white"
                : "border-gray-200 text-gray-500 hover:border-secondary-300 hover:text-secondary-700"
            }`}
          >
            <CopyPlus className="h-3 w-3" />
            {editingSlots ? t("planning.card.slotsDone") : t("planning.card.slots")}
          </button>
        )}
        {context === "slot" && unassignable && (
          <button
            type="button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onUnassign?.(card);
            }}
            title={t("planning.card.unassignHint")}
            className="flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 transition hover:border-orange-300 hover:text-orange-700 disabled:opacity-50"
          >
            <Truck className="h-3 w-3" />
            {t("planning.card.unassign")}
          </button>
        )}
      </div>

      {/*
        Opened out, below everything the card already says. Clicks inside it are
        stopped here: the card itself picks the container up when clicked, and
        looking at a map is not asking to move a box.
      */}
      {mapOpen && (
        <div onClick={(event) => event.stopPropagation()}>
          <PlanningCardMap card={card} />
        </div>
      )}

      {/*
        Where this container actually is, in words.

        The card used to carry the step count alone — "Under way 4/6" — which
        says how far through a run a box is but not what it is *doing*, and the
        status was hidden in the `title` of a 8px dot nobody hovers. The two
        together are what a dispatcher reads: the word says where it is, the
        count says how much is left.

        `completed` gets its own treatment rather than a twelfth variation of
        the same amber line, and this is the part that was actively misleading:
        a finished run read "Under way 6/6". Somebody would then try to move it
        and be refused by `findPlaceBlocker`, which closes the board to a
        completed container — a rule the screen had given them no way to know
        about. Green, named, and visibly a different kind of thing from a run in
        progress.
      */}
      {card.status === "completed" ? (
        <div className="mt-1.5 flex items-center justify-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-[10px] font-bold text-green-700">
          <CheckCircle2 className="h-3 w-3 shrink-0" />
          {t("planning.status.completed")}
        </div>
      ) : card.started ? (
        <div className="mt-1 flex min-w-0 items-center gap-1 text-[10px] font-medium text-amber-700">
          <span className="truncate">
            {t(`planning.status.${card.status}`, {
              defaultValue: card.status,
            })}
          </span>
          <span className="shrink-0 font-mono text-amber-600">
            {card.progress.done}/{card.progress.total}
          </span>
        </div>
      ) : null}
    </div>
  );
}
